// src/services/musicService.js
import api from './api';
import { logger } from '../utils/logger';

const STREAM_URL_TTL_MS = 4 * 60 * 1000;

const streamUrlCache = new Map();
const streamUrlRequests = new Map();

const ensureAuthenticatedSession = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authenticated user found');
  }
};

const getCachedStreamUrl = (songId) => {
  const cached = streamUrlCache.get(songId);
  if (!cached || cached.expiresAt <= Date.now()) {
    streamUrlCache.delete(songId);
    return null;
  }
  return cached.url;
};

export const clearStreamUrlCache = () => {
  streamUrlCache.clear();
  streamUrlRequests.clear();
};

export const getStreamingUrl = async (songId) => {
  if (!songId) {
    throw new Error('Song ID is required for streaming');
  }

  const cachedUrl = getCachedStreamUrl(songId);
  if (cachedUrl) {
    return { url: cachedUrl, cached: true };
  }

  if (streamUrlRequests.has(songId)) {
    return streamUrlRequests.get(songId);
  }

  const request = (async () => {
    try {
      await ensureAuthenticatedSession();

      const streamEndpoint = new URL(`/api/music/stream/${songId}`, api.defaults.baseURL).toString();
      const response = await api.get(streamEndpoint);
      const url = response.data?.url;

      if (!url) {
        throw new Error('Backend did not return a valid streaming URL');
      }

      streamUrlCache.set(songId, {
        url,
        expiresAt: Date.now() + STREAM_URL_TTL_MS
      });

      return { url, cached: false };
    } catch (error) {
      logger.error('Failed to get streaming URL', {
        error: error.message,
        songId
      });
      throw error;
    } finally {
      streamUrlRequests.delete(songId);
    }
  })();

  streamUrlRequests.set(songId, request);
  return request;
};
